import { GpmPayConfigError } from '../core/errors.js';
import type { HttpClient } from '../core/http.js';
import { toIsoString } from '../core/query.js';
import type { RequestOptions } from '../types/common.js';
import type {
  SimulatedTransactionResult,
  SimulateTransactionParams,
} from '../types/transaction.js';

export interface SimulatorOptions extends RequestOptions {
  /**
   * Simulated transactions are a development tool. The SDK refuses to call
   * this against a production base URL unless you opt in explicitly.
   */
  allowOnProduction?: boolean;
}

function isNonProductionBaseUrl(baseUrl: string): boolean {
  return /localhost|127\.0\.0\.1|sandbox|\.local(?::|\/|$)/i.test(baseUrl);
}

/**
 * Generate fake inbound transfers for local development and integration tests.
 *
 * Scope: `transactions:read`. That reads oddly for a route that *creates* a
 * transaction — it is a deliberate backend trade-off to avoid minting a new
 * scope. Simulated transactions always carry `source: 'SIMULATED'` and only
 * fan out to endpoints with `fireOnSimulated` enabled.
 */
export class SimulatorResource {
  constructor(private readonly http: HttpClient) {}

  private assertSafeEnvironment(options: SimulatorOptions): void {
    if (options.allowOnProduction === true) return;
    if (isNonProductionBaseUrl(this.http.baseUrl)) return;

    throw new GpmPayConfigError(
      `simulator: refusing to create a simulated transaction against ${this.http.baseUrl}. ` +
        'Point the client at localhost or the sandbox (`new GpmPay({ sandbox: true })`), ' +
        'or pass { allowOnProduction: true } if you really mean it.',
    );
  }

  /**
   * Create a simulated bank transaction.
   *
   * Returns the envelope the route actually sends — `{ transaction,
   * historyIds }`, not a bare `Transaction`. Read `result.transaction` for the
   * ledger row and `result.historyIds.length` to confirm a webhook was queued.
   *
   * `async` so guard failures reject rather than throwing synchronously.
   */
  async createTransaction(
    params: SimulateTransactionParams,
    options: SimulatorOptions = {},
  ): Promise<SimulatedTransactionResult> {
    this.assertSafeEnvironment(options);

    if (!Number.isInteger(params.amount) || params.amount < 1) {
      throw new GpmPayConfigError(
        `simulator: amount must be an integer number of VND >= 1, received ${String(params.amount)}.`,
      );
    }

    const body: Record<string, unknown> = {
      bankAccountId: params.bankAccountId,
      amount: params.amount,
      transferContent: params.transferContent,
    };
    if (params.type !== undefined) body.type = params.type;
    if (params.referenceCode !== undefined) {
      body.referenceCode = params.referenceCode;
    }
    if (params.counterAccount !== undefined) {
      body.counterAccount = params.counterAccount;
    }
    if (params.counterName !== undefined) body.counterName = params.counterName;
    const transactionTime = toIsoString(params.transactionTime);
    if (transactionTime !== undefined) body.transactionTime = transactionTime;

    const { allowOnProduction: _ignored, ...requestOptions } = options;
    return await this.http.request<SimulatedTransactionResult>(
      'POST',
      '/simulator/transactions',
      { ...requestOptions, body },
    );
  }
}
